import { useState, useRef, useEffect, useCallback } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import { createPortal } from "react-dom";

const DEFAULT_COLUMN_LABELS = {
  frontorderid: "Front Order ID",
  orderType: "Order Type",
  orderStatus: "Order Status",
  orderSendBy: "Order Send By",
  invoiceNo: "Invoice No",
  source: "Source",
  tillname: "Till Name",
  customer: "Customer",
  orderDate: "Order Date",
  paymentStatus: "Payment Status",
  paymentMethod: "Payment Method",
  totalAmount: "Total Amount",
  fillBy: "Fill By",
};

const formatColumnLabel = (column, labels = {}) => {
  if (labels[column]) return labels[column];
  if (DEFAULT_COLUMN_LABELS[column]) return DEFAULT_COLUMN_LABELS[column];

  // Strip entity prefix (e.g. Voucher_Name -> Name, MenuItem_SalePrice -> SalePrice)
  const withoutPrefix = column.includes("_")
    ? column.replace(/^[A-Za-z0-9]+_/, "")
    : column;

  return withoutPrefix
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getPanelPosition = (buttonEl) => {
  if (!buttonEl) return { top: 0, left: 0 };
  const rect = buttonEl.getBoundingClientRect();
  const panelWidth = 220;
  const gutter = 8;
  let left = rect.right - panelWidth;
  left = Math.max(gutter, Math.min(left, window.innerWidth - panelWidth - gutter));
  return {
    top: rect.bottom + 6,
    left,
  };
};

/**
 * @param {object} props
 * @param {Record<string, boolean>} props.visibleColumns
 * @param {(cols: Record<string, boolean>) => void} props.setVisibleColumns
 * @param {string[]} [props.columnOrder]
 * @param {Record<string, string>} [props.columnLabels]
 */
const ColumnSelection = ({
  visibleColumns,
  setVisibleColumns,
  columnOrder,
  columnLabels = {},
}) => {
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const isDarkMode = useDarkMode();
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const columns = columnOrder || Object.keys(visibleColumns || {});

  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) return;
    setPanelPosition(getPanelPosition(buttonRef.current));
  }, []);

  const handleColumnSelectionButton = () => {
    if (!showColumnPanel && buttonRef.current) {
      setPanelPosition(getPanelPosition(buttonRef.current));
    }
    setShowColumnPanel((prev) => !prev);
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column],
    });
  };

  const selectAllColumns = () => {
    const allSelected = {};
    Object.keys(visibleColumns).forEach((column) => {
      allSelected[column] = true;
    });
    setVisibleColumns(allSelected);
  };

  const deselectAllColumns = () => {
    const noneSelected = {};
    Object.keys(visibleColumns).forEach((column) => {
      noneSelected[column] = false;
    });
    setVisibleColumns(noneSelected);
  };

  useEffect(() => {
    if (!showColumnPanel) return undefined;

    updatePanelPosition();

    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowColumnPanel(false);
      }
    };

    const handleViewportChange = () => updatePanelPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [showColumnPanel, updatePanelPosition]);

  return (
    <div className="position-relative d-flex align-items-center">
      <button
        type="button"
        onClick={handleColumnSelectionButton}
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm d-flex align-items-center"
        style={{
          height: "32px",
          fontSize: "12px",
          border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
          borderRadius: "8px",
          backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
          color: isDarkMode ? "#ffffff" : "#495057",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 16px",
        }}
        title="Select Columns"
        aria-expanded={showColumnPanel}
        aria-haspopup="dialog"
      >
        <i className="fa fa-columns me-1" aria-hidden="true" />
        Columns
      </button>

      {showColumnPanel &&
        createPortal(
          <div
            ref={panelRef}
            className="column-selection-portal-panel"
            role="dialog"
            aria-label="Select columns"
            style={{
              position: "fixed",
              top: `${panelPosition.top}px`,
              left: `${panelPosition.left}px`,
              backgroundColor: isDarkMode ? "#2d3436" : "white",
              border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "12px",
              width: "220px",
              zIndex: 20000,
              boxShadow: isDarkMode
                ? "0 8px 24px rgba(0,0,0,0.45)"
                : "0 8px 24px rgba(15,23,42,0.12)",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
              style={{ borderBottomColor: isDarkMode ? "#404040" : "#dee2e6" }}
            >
              <h6
                className="mb-0 fw-semibold"
                style={{
                  fontSize: "13px",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                Select Columns
              </h6>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={selectAllColumns}
                  style={{
                    fontSize: "10px",
                    padding: "2px 6px",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    backgroundColor: isDarkMode ? "#232B2B" : "#ffffff",
                    borderColor: isDarkMode ? "#404040" : "#dee2e6",
                  }}
                  title="Select All"
                >
                  All
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={deselectAllColumns}
                  style={{
                    fontSize: "10px",
                    padding: "2px 6px",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                    borderColor: isDarkMode ? "#404040" : "#dee2e6",
                  }}
                  title="Deselect All"
                >
                  None
                </button>
              </div>
            </div>

            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
              {columns.length === 0 ? (
                <div
                  style={{
                    fontSize: "12px",
                    color: isDarkMode ? "#adb5bd" : "#6c757d",
                    padding: "4px 0",
                  }}
                >
                  No columns available.
                </div>
              ) : (
                columns.map((column) => (
                  <div key={column} className="d-flex align-items-center py-1">
                    <input
                      type="checkbox"
                      id={`column-${column}`}
                      checked={Boolean(visibleColumns[column])}
                      onChange={() => toggleColumnVisibility(column)}
                      className="me-2"
                      style={{
                        transform: "scale(0.9)",
                        accentColor: isDarkMode ? "#30d6a4" : "#007bff",
                      }}
                    />
                    <label
                      htmlFor={`column-${column}`}
                      className="mb-0"
                      style={{
                        fontSize: "12px",
                        cursor: "pointer",
                        flex: 1,
                        lineHeight: "1.3",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      {formatColumnLabel(column, columnLabels)}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ColumnSelection;
